<script setup lang="ts">
import { COMMON_RESET_PAGE_ON, commonFilterFields, enumArrayField, PaginationComponent, useQueryFilter } from '@metapic-apps/shared'

import { useGetReturnAds } from '@/api/return-ads.api'
import PlusIcon from '@/assets/icons/add-plus.svg?component'
import { FilterComponent } from '@/components/Filter'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import ReturnAdListItem from '@/components/ReturnAdListItem/ReturnAdListItem.vue'
import { ReturnAdStatusEnum } from '@/lib/types/return-ad.types'
import { type ReturnAdsFilterType } from '@/lib/types/return-ads.types'
import { useUserStore } from '@/stores/user'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, PageWrapperComponent, TypographyComponent } from '@/ui-library'

const userStore = useUserStore()
const { filter, reset } = useQueryFilter<ReturnAdsFilterType>(
  {
    page: commonFilterFields.page,
    sortBy: commonFilterFields.sortBy,
    query: commonFilterFields.query,
    advertiserGroup: commonFilterFields.advertiserGroup,
    advertiser: commonFilterFields.advertiser,
    statuses: enumArrayField('statuses', Object.values(ReturnAdStatusEnum)),
  },
  {
    page: 1,
    sortBy: 'created_at:desc',
    advertiserGroup: {
      id: userStore.user.storeGroupId,
      name: '',
    },
  },
  { resetPageOn: COMMON_RESET_PAGE_ON, cleared: { page: 1, sortBy: 'created_at:desc' } },
)
const { data, isLoading, totalPages, error } = useGetReturnAds(filter)
const sortingItems = [
  { label: 'Creation date', value: 'created_at' },
  { label: 'End date', value: 'ends_at' },
  { label: 'Views', value: 'current_views' },
  { label: 'Clicks', value: 'current_clicks' },
  { label: 'Click-through rate', value: 'click_through_rate' },
  { label: 'Orders', value: 'order_count' },
  { label: 'Conversion rate', value: 'conversion_rate' },
  { label: 'Orders value', value: 'order_value' },
  { label: 'Earnings', value: 'earnings' },
]
const moreFiltersItems = [
  { value: ReturnAdStatusEnum.Active, label: 'Active' },
  { value: ReturnAdStatusEnum.Scheduled, label: 'Scheduled' },
  { value: ReturnAdStatusEnum.Paused, label: 'Paused' },
  { value: ReturnAdStatusEnum.Ended, label: 'Ended' },
]
</script>

<template>
  <HeaderComponent>
    <FlexComponent :gap="16">
      <ButtonComponent variant="secondary" to="/return-ads/create">
        <template #iconLeft>
          <el-icon :size="18">
            <PlusIcon />
          </el-icon>
        </template>
        Create return ad
      </ButtonComponent>
    </FlexComponent>
  </HeaderComponent>
  <main class="return-ad-view__wrapper">
    <PageWrapperComponent>
      <BoxComponent :mb="32">
        <FilterComponent
          v-model="filter"
          :sorting-items="sortingItems"
          :more-filters-items="moreFiltersItems"
          :is-return-ad-enabled="true"
          show-advertiser-selector
          show-more-filters-selector
          show-sorting-selector
          placeholder="Search return ad"
          @clear="reset"
        />
      </BoxComponent>
      <BoxComponent v-if="error" :mb="24">
        <el-alert :title="error.message" type="error" />
      </BoxComponent>
      <FlexComponent v-if="!isLoading && data && data?.length === 0" justify="center">
        <TypographyComponent variant="body-large"> There are no campaigns that match your filters </TypographyComponent>
      </FlexComponent>
      <LoadingComponent v-if="isLoading" :size="64" />
      <FlexComponent v-if="!isLoading && data && data?.length > 0" :gap="16" direction="column">
        <div v-for="item in data" :key="item.id">
          <ReturnAdListItem :item="item" />
        </div>
      </FlexComponent>
    </PageWrapperComponent>
    <BoxComponent v-if="totalPages !== 0 && data && data?.length > 0" :pt="32" :pb="32" class="return-ad-view__pagination">
      <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
    </BoxComponent>
  </main>
</template>
<style scoped lang="scss"></style>
